import { h, Component } from 'preact';
import Store from 'store/store';

import { getData } from 'utils/dynamicData';
import { getYoutubePlaylist } from 'utils/youtube';
import { getAllMovieStatuses, getNextShowtimes } from 'utils/showtimes';
import { parseDate } from 'utils/utils';
import formatDateTime from 'utils/formatDateTime';

import Hero from 'components/hero/hero';
import MoviesList from 'components/scrollList/moviesList';
import TrailersList from 'components/scrollList/trailersList';
import PromoTouts from 'components/promoTouts/promoTouts';
import Newsletter from 'components/newsletter/newsletter';

import s from 'components/pages/landing/landing.sss';

const comingSoonTitleLink = { label: 'See all upcoming movies', page: 'releases' };

export default class Landing extends Component {

	constructor(props) {
		super(props);
		this.state = {
			loaded: false,
			trailersLoaded: false,
			heroMovies: [],
			inRelease: [],
			comingSoon: [],
			nextShowtimes: {},
		};
		if (typeof window !== 'undefined') {
			this.loadData();
			this.getTrailers();
		}
	}

	componentDidUpdate() {
		if (this.checkAnchorAfterRender) {
			this.checkAnchorAfterRender = false;
			this.checkAnchor();
		}
	}

	sortBy(toSort, sortBy) {
		if (!toSort) return [];
		if (typeof toSort[0]?.[sortBy] === 'boolean') {
			return toSort.sort((a, b) => {
				if (a.pinned && !b.pinned) {
					return -1;
				}
				if (!a.pinned && b.pinned) {
					return 1;
				}
				return 0;
			});
		}
		return toSort.sort((a, b) => {
			if (a[sortBy] < b[sortBy]) {
				return -1;
			}
			if (a[sortBy] > b[sortBy]) {
				return 1;
			}
			return 0;
		});
	}

	checkAnchor() {
		let anchor = location.hash.slice(1);
		if (!anchor) return;
		let element = this.base.querySelector(`#${anchor}`);
		if (element) {
			element.scrollIntoView({ block: 'start' });
			// Ignore scroll restoration if we've succesfully scrolled to an element?
			// history.scrollRestoration = 'manual';
		}
	}

	async loadData() {
		// TODO: error handling (/ loading state ?)
		const data = await getData('landing');

		const heroMovies = (data?.hero?.list || []).map(item => ({ ...item, movie: data.movies?.[item.movie] }));
		this.setState({ heroMovies });

		const movies = Object.values(data?.movies || {});
		const statuses = await this.getMovieStatuses(movies);
		movies.forEach((movie) => {
			movie.status = statuses[movie.id];
			movie.releaseDateParsed = parseDate(movie?.dates?.release) || null;
		});

		const today = formatDateTime(new Date(), 'yyyy-MM-dd');
		const { inRelease, comingSoon } = movies.sort(this.sortMoviesByReleaseDate).reduce((acc, movie) => {
			// if (!movie.releaseDateParsed) return acc;
			const overrides = data?.carousels?.overrides?.[movie.id];
			let type;
			const status = statuses[movie.id];
			if (!status) {
				type = today >= movie.dates.release ? 'inRelease' : 'comingSoon';
			} else if (status.theaters === 'comingSoon' || status.theaters === 'preSale') {
				type = 'comingSoon';
			} else if (status.theaters === 'inTheaters' || status.homeEnt === 'homeEnt') {
				type = 'inRelease';
			}
			if (!type) return acc;
			const pinned = data?.carousels?.[type]?.pinned === movie.id;
			const newData = { movie, pinned, ...overrides };
			acc[type][newData.pinned ? 'unshift' : 'push']({ movie, pinned, ...overrides });
			return acc;
		}, { inRelease: [], comingSoon: [] });

		this.checkAnchorAfterRender = true;
		this.setState({
			loaded: true,
			inRelease,
			comingSoon,
		});

		try {
			// TODO: only for movies with a showtimes page
			const showtimesIdsToMovieIds = Object.fromEntries(movies.map((movie) => {
				if (!movie.showtimesId) return;
				// if (movie.status?.theaters !== 'inTheaters' && movie.status?.theaters !== 'preSale') return;
				return [movie.showtimesId, movie.id];
			}).filter(Boolean));
			const screeningsData = await getNextShowtimes(Object.keys(showtimesIdsToMovieIds));

			const now = Date.now();
			const today = formatDateTime(new Date(), 'yyyy-MM-dd');
			let tomorrowRaw = new Date();
			tomorrowRaw.setDate(tomorrowRaw.getDate() + 1);
			const tomorrow = formatDateTime(tomorrowRaw, 'yyyy-MM-dd');

			const nextShowtimes = Object.fromEntries(Object.entries(screeningsData).map(([id, screenings]) => {
				if (!showtimesIdsToMovieIds[id]) return;
				screenings?.forEach(screening => {
					screening.dateTime = parseDate(`${screening.date} ${screening.time}`);
					// TODO: based on user locale?
					let format = 'ddd h:mmtt';
					if (screening.date === today) {
						format = '"Today" h:mmtt';
					} else if (screening.date === tomorrow) {
						format = '"Tomorrow" h:mmtt';
					} else if (screening.dateTime - now >= 6 * 24 * 60 * 60 * 1000) {
						format = 'MMM dd h:mmtt';
					}
					screening.label = `Next showing ${formatDateTime(screening.dateTime, format)} at ${screening.name}`;
				});
				return [showtimesIdsToMovieIds[id], screenings];
			}).filter(Boolean));
			this.setState({ nextShowtimes });
		} catch (err) {
			console.error(err);
		}
	}

	async getTrailers() {
		const trailers = await getYoutubePlaylist();
		this.setState({ trailers, trailersLoaded: true });
	}

	async getMovieStatuses(movies) {
		const movieStatuses = await getAllMovieStatuses(movies);
		Store.get().set({ movieStatuses });
		return movieStatuses;
	}

	sortMoviesByReleaseDate(a, b) {
		// const aDate = a?.dates?.release || '';
		// const bDate = b?.dates?.release || '';
		// return bDate.localeCompare(aDate);

		// movies no longer in cinemas always go last
		const aTheaters = a?.status?.theaters !== 'unknown';
		const bTheaters = b?.status?.theaters !== 'unknown';
		if (aTheaters !== bTheaters) return aTheaters ? -1 : 1;

		// Closest release date first (future or past)
		const ra = a?.releaseDateParsed;
		const rb = b?.releaseDateParsed;
		if (!ra && !rb) return 0;
		if (!ra) return 1;
		if (!rb) return -1;
		const now = Date.now();
		return Math.abs(now - ra) - Math.abs(now - rb);
	}

	render(props, state) {
		const { loaded, trailersLoaded, heroMovies, inRelease, comingSoon, trailers } = state;
		return (
			<div class={s.wrapper}>
				<Hero movies={heroMovies} nextShowtimes={state.nextShowtimes} key="hero" />
				{(!loaded || !!inRelease?.length) && (
					<MoviesList
						id="in-release"
						title={'In\nRelease'}
						items={inRelease}
						nextShowtimes={state.nextShowtimes}
						loaded={loaded}
						key="in-release"
					/>
				)}
				{(!loaded || !!comingSoon?.length) && (
					<MoviesList
						id="coming-soon"
						title={'Coming\nSoon'}
						items={comingSoon}
						titleLink={comingSoonTitleLink}
						nextShowtimes={state.nextShowtimes}
						loaded={loaded}
						key="coming-soon"
					/>
				)}
				<TrailersList id="trailers" title="Trailers and Featured Videos" items={trailers} loaded={trailersLoaded} key="trailers" />
				<PromoTouts section="promo-touts" key="promo" />
				<Newsletter key="newsletter" />
			</div>
		);
	}
}